import { ClipboardPaste, MonitorSmartphone, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { OpenTab, SummaryLang, SummaryRecord } from '../shared/api';
import { pickForPreference } from '../shared/lang';
import { parseYouTubeVideoId } from '../shared/youtube-url';
import { CodexLoginDialog, type CodexConnectedResult } from './components/CodexLoginDialog';
import { Header, type CodexStatus } from './components/Header';
import { HistoryList } from './components/HistoryList';
import { ResetPasswordView } from './components/ResetPasswordView';
import { ResultCard, type ErrorAction } from './components/ResultCard';
import { SignInDialog, type SignInView } from './components/SignInDialog';
import { TabList, tabTitle, type TabStatus } from './components/TabList';
import { useToast } from './components/Toasts';
import { UrlForm, type UrlFormProps } from './components/UrlForm';
import { ApiRequestError, deleteCodexAuth, getCodexModels, saveCodexAuth } from './lib/api';
import { authErrorMessage, signOut } from './lib/authClient';
import { canReadClipboard, copyText, readClipboardText } from './lib/clipboard';
import { useHistory, useLocalCodexLogin, useMe, useSettings } from './lib/hooks';
import { getPushTarget, onLaunch, requestNotifications, showNotification, useInstallPrompt, useNotificationState } from './lib/pwa';
import { absoluteVideoUrl, parseRoute, videoPath, type Route } from './lib/route';
import {
  isOlderCodexAuth,
  loadCodexLogin,
  loadSettings,
  markMigrationOffered,
  wasMigrationOffered,
  type HistoryEntry,
} from './lib/storage';
import { neighborTab } from './lib/tabs';
import { APP_NAME, CODEX_TEXTS } from './lib/texts';
import { useOpenTabs } from './lib/useOpenTabs';
import { useSummaryJobs, type Job, type JobError } from './lib/useSummaryJob';

type Hint = UrlFormProps['hint'];

const INVALID_HINT = 'Das ist kein YouTube-Link. Kopiere die Adresse eines Videos, z. B. https://youtu.be/dQw4w9WgXcQ';
const UNRECOGNIZED_HINT = 'In der Adresse wurde kein YouTube-Video erkannt. Füge den Link eines Videos ein.';

function currentRoute(): Route {
  return parseRoute(window.location.pathname, window.location.search);
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

function entryToRecord(entry: HistoryEntry): SummaryRecord {
  return {
    videoId: entry.videoId,
    lang: entry.lang,
    title: entry.title,
    channel: entry.channel,
    markdown: entry.markdown,
    ...(entry.model ? { model: entry.model } : {}),
    createdAt: entry.createdAt,
    cached: false,
    ...(entry.sourceLang ? { sourceLang: entry.sourceLang } : {}),
  };
}

function isEditable(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
}

function shorten(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

function scrollToTop() {
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
}

export function App() {
  const toast = useToast();
  const [route, setRoute] = useState<Route>(currentRoute);
  const { me, unconfirmed: meUnconfirmed, refresh: refreshMe, whenReady: whenMeReady } = useMe();
  const [settings, updateSettings] = useSettings();
  const localCodex = useLocalCodexLogin();
  const recent = useHistory();
  // undefined while the account is unknown (also when /api/me failed): tab changes are held, nothing is discarded.
  const openTabs = useOpenTabs(me === null || meUnconfirmed ? undefined : (me.user?.id ?? null));
  const installPrompt = useInstallPrompt();
  const [notificationPermission, refreshNotificationPermission] = useNotificationState();

  const [input, setInput] = useState('');
  const [hint, setHint] = useState<Hint>(null);
  const [codexDialog, setCodexDialog] = useState({ open: false, resume: false });
  const [signInDialog, setSignInDialog] = useState<{ open: boolean; view: SignInView; resume: boolean; notice?: string }>({
    open: false,
    view: 'sign-in',
    resume: false,
  });
  const [migrationHidden, setMigrationHidden] = useState(false);
  /** Videos whose summary finished while another one was shown. */
  const [unread, setUnread] = useState<ReadonlySet<string>>(() => new Set());

  const inputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLElement>(null);

  const user = me?.user ?? null;
  const signedIn = !!user;
  const serverCodex = !!user && !!me?.codex.stored;
  const currentVideoId = route.kind === 'video' ? route.videoId : undefined;

  const codexStatus: CodexStatus = serverCodex
    ? { connected: true, account: me?.codex.account, location: 'server' }
    : localCodex.login
      ? { connected: true, account: localCodex.login.account, location: 'browser' }
      : { connected: false };

  // Latest values for callbacks that outlive a render.
  const latest = useRef({ me, settings, recent, localCodex, openTabs, route });
  useEffect(() => {
    latest.current = { me, settings, recent, localCodex, openTabs, route };
  });

  const markUnread = useCallback((videoId: string, value: boolean) => {
    setUnread((previous) => {
      if (previous.has(videoId) === value) return previous;
      const next = new Set(previous);
      if (value) next.add(videoId);
      else next.delete(videoId);
      return next;
    });
  }, []);

  const isVisible = (videoId: string) => {
    const current = latest.current.route;
    return current.kind === 'video' && current.videoId === videoId;
  };

  // ------------------------------------------------------------------------
  // Summary jobs (one per video; the one of the current route is shown)

  /**
   * A generated summary can be read: it is being written. Announced when it is out of sight:
   * with a system notification while the app is in the background, with a toast while another
   * tab is shown. (The server pushes the same notification while Android suspends the app.)
   */
  const onSummaryWriting = (job: Job) => {
    const documentHidden = document.visibilityState === 'hidden';
    if (isVisible(job.videoId) && !documentHidden) return;
    const title = shorten(job.meta?.title || 'Video', 80);
    if (documentHidden) {
      if (latest.current.settings.notify) {
        void showNotification({ title: 'Zusammenfassung bereit', body: title, url: videoPath(job.videoId), tag: job.videoId });
      }
      return;
    }
    toast(`Bereit: „${title}“`, { action: { label: 'Öffnen', onClick: () => openVideo(job.videoId) } });
  };

  const enableNotifications = useCallback(async () => {
    updateSettings({ notify: true });
    const granted = await requestNotifications();
    refreshNotificationPermission();
    if (granted) void getPushTarget(); // subscribe now; requests then carry the subscription
    toast(
      granted
        ? 'Du bekommst eine Benachrichtigung, sobald eine Zusammenfassung bereit ist.'
        : 'Benachrichtigungen sind nicht erlaubt. Du kannst sie in den Browser-Einstellungen freigeben.',
    );
  }, [updateSettings, refreshNotificationPermission, toast]);

  const onJobError = useCallback(
    (error: JobError, job: Job) => {
      // Dialogs only for the summary on screen; background tabs show the error when opened.
      const visible = isVisible(job.videoId);
      if (error.code === 'codex_auth_required') {
        if (visible) setCodexDialog({ open: true, resume: true });
      } else if (error.code === 'codex_auth_invalid') {
        const { me: currentMe, localCodex: local } = latest.current;
        if (currentMe?.user && currentMe.codex.stored) {
          void deleteCodexAuth()
            .catch(() => {})
            .then(() => refreshMe());
        } else {
          local.clear();
        }
        if (visible) setCodexDialog({ open: true, resume: true });
      } else if (error.code === 'login_required') {
        void refreshMe();
      }
    },
    [refreshMe],
  );

  const loadCodexModels = useCallback(async () => {
    const { me: currentMe, localCodex: local } = latest.current;
    const browserLogin = !(currentMe?.user && currentMe.codex.stored);
    const result = await getCodexModels(browserLogin ? loadCodexLogin()?.auth : undefined);
    // Never go back to an older copy (refresh tokens are single-use).
    if (result.auth && !isOlderCodexAuth(result.auth, loadCodexLogin()?.auth)) local.save(result.auth);
    return result;
  }, []);

  const { jobs, manager } = useSummaryJobs({
    getContext: async () => {
      const current = await whenMeReady();
      const signed = !!current.user;
      const stored = loadSettings();
      return {
        signedIn: signed,
        serverCodex: signed && current.codex.stored,
        localAuth: loadCodexLogin()?.auth ?? null,
        mode: signed ? stored.transcriptMode : 'client',
        model: stored.model,
        effort: stored.effort,
      };
    },
    getLocalAuth: () => loadCodexLogin()?.auth ?? null,
    // Lets the server push "summary ready" while Android has the app suspended.
    getPushTarget: () => (latest.current.settings.notify ? getPushTarget() : Promise.resolve(undefined)),
    onAuthRefresh: (auth) => latest.current.localCodex.save(auth),
    onDone: (record, job) => {
      const sourceLang = record.sourceLang ?? job.sourceLang;
      latest.current.recent.upsert({
        videoId: record.videoId || job.videoId,
        lang: record.lang || job.lang || latest.current.settings.langPref.fallback,
        title: record.title || job.meta?.title || '',
        channel: record.channel || job.meta?.channel || '',
        markdown: record.markdown,
        ...(record.model ? { model: record.model } : {}),
        createdAt: record.createdAt || new Date().toISOString(),
        viewedAt: new Date().toISOString(),
        ...(job.meta?.durationSeconds ? { durationSeconds: job.meta.durationSeconds } : {}),
        ...(sourceLang ? { sourceLang } : {}),
      });
      if (!isVisible(job.videoId)) markUnread(job.videoId, true);
    },
    onWriting: onSummaryWriting,
    onError: onJobError,
  });

  const job = currentVideoId ? jobs[currentVideoId] : undefined;

  const tabStatus = useCallback(
    (videoId: string): TabStatus => {
      const tabJob = jobs[videoId];
      if (!tabJob) return undefined;
      if (tabJob.status === 'running') return tabJob.waiting ? 'waiting' : 'running';
      if (tabJob.status === 'error') return 'error';
      if (tabJob.status === 'done' && unread.has(videoId)) return 'unread';
      return undefined;
    },
    [jobs, unread],
  );

  // Tabs learn title, channel and the resolved language from their jobs.
  const sentTabUpdates = useRef(new Map<string, string>());
  useEffect(() => {
    for (const tab of openTabs.tabs) {
      const tabJob = jobs[tab.videoId];
      if (!tabJob) continue;
      const title = tabJob.meta?.title || tabJob.summary?.title || '';
      const channel = tabJob.meta?.channel || tabJob.summary?.channel || '';
      const lang = tabJob.lang;
      const changed =
        (!!lang && lang !== tab.lang) || (!!title && title !== tab.title) || (!!channel && channel !== tab.channel);
      if (!changed) continue;
      // Sent once per value: a server that normalises a title must not cause a loop.
      const signature = JSON.stringify([lang, title, channel]);
      if (sentTabUpdates.current.get(tab.videoId) === signature) continue;
      sentTabUpdates.current.set(tab.videoId, signature);
      openTabs.open({ videoId: tab.videoId, lang, title, channel });
    }
  }, [jobs, openTabs]);

  // ------------------------------------------------------------------------
  // Routing (address bar ⇄ state)

  const navigate = useCallback((path: string, mode: 'push' | 'replace') => {
    const url = new URL(path, window.location.origin);
    const target = url.pathname + url.search;
    if (target !== currentPath()) {
      if (mode === 'push') window.history.pushState(null, '', target);
      else window.history.replaceState(null, '', target);
    }
    setRoute(parseRoute(url.pathname, url.search));
  }, []);

  /** `lang` only when asked for explicitly: without it the summary follows the language preference. */
  const openVideo = useCallback(
    (videoId: string, lang?: SummaryLang, mode: 'push' | 'replace' = 'push') => {
      navigate(videoPath(videoId, lang), mode);
    },
    [navigate],
  );

  const focusResult = useCallback(() => {
    // Move focus to the result (closes the on-screen keyboard on phones).
    window.requestAnimationFrame(() => resultRef.current?.focus({ preventScroll: true }));
    scrollToTop();
  }, []);

  const goHome = useCallback(() => {
    navigate('/', 'push');
    setHint(null);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [navigate]);

  // Initial address: normalise it (replace), explain unrecognised input.
  useEffect(() => {
    const initial = currentRoute();
    if (initial.kind === 'video') {
      window.history.replaceState(null, '', videoPath(initial.videoId, initial.lang));
    } else if (initial.kind === 'home' && initial.unrecognized) {
      setInput(initial.unrecognized.slice(0, 500));
      setHint({ tone: 'error', text: UNRECOGNIZED_HINT });
      window.history.replaceState(null, '', '/');
    }
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(currentRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Route → tab + job. Jobs of other videos keep running in the background.
  useEffect(() => {
    if (route.kind !== 'video') return;
    const { videoId } = route;
    const { recent: history, settings: currentSettings, openTabs: tabs } = latest.current;
    const entries = history.entries.filter((e) => e.videoId === videoId);
    const existing = manager.get(videoId);
    const tab = tabs.tabs.find((t) => t.videoId === videoId);

    // Every opened video becomes (or stays) a tab.
    tabs.open({
      videoId,
      lang: route.lang ?? existing?.lang,
      title: existing?.meta?.title || entries[0]?.title,
      channel: existing?.meta?.channel || entries[0]?.channel,
    });
    markUnread(videoId, false);

    if (existing && (!route.lang || existing.lang === route.lang || existing.requestedLang === route.lang)) return;

    // Explicit language; else the tab's (it may have been switched there); else the preference.
    const lang = route.lang ?? tab?.lang;
    const pref = currentSettings.langPref;
    const entry = lang ? entries.find((e) => e.lang === lang) : pickForPreference(entries, pref);
    if (entry) {
      manager.show(entryToRecord(entry), entry.durationSeconds ? { durationSeconds: entry.durationSeconds } : undefined);
      history.upsert({ ...entry, viewedAt: new Date().toISOString() });
    } else {
      manager.start({ videoId, lang, langPref: pref });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  const view: 'reset' | 'idle' | 'result' =
    route.kind === 'reset-password' ? 'reset' : route.kind === 'video' && job ? 'result' : 'idle';

  // Document title.
  useEffect(() => {
    const title = view === 'result' ? job?.meta?.title : undefined;
    document.title = title ? `${title} – ${APP_NAME}` : view === 'reset' ? `Passwort festlegen – ${APP_NAME}` : APP_NAME;
  }, [view, job?.meta?.title]);

  // ------------------------------------------------------------------------
  // Tabs

  /** The language a video opens in without `?lang=` (its job, its tab, else the history pick). */
  const automaticLang = useCallback(
    (videoId: string): SummaryLang | undefined => {
      const { recent: history, settings: currentSettings, openTabs: tabs } = latest.current;
      return (
        manager.get(videoId)?.lang ??
        tabs.tabs.find((t) => t.videoId === videoId)?.lang ??
        pickForPreference(
          history.entries.filter((e) => e.videoId === videoId),
          currentSettings.langPref,
        )?.lang
      );
    },
    [manager],
  );

  const openTab = useCallback(
    (tab: OpenTab) => {
      setHint(null);
      openVideo(tab.videoId);
      focusResult();
    },
    [openVideo, focusResult],
  );

  const closeTab = useCallback(
    (videoId: string) => {
      const before = latest.current.openTabs.tabs;
      const next = neighborTab(before, videoId);
      const wasCurrent = isVisible(videoId);
      const previousPath = currentPath();
      latest.current.openTabs.close(videoId);
      manager.remove(videoId);
      sentTabUpdates.current.delete(videoId);
      markUnread(videoId, false);
      if (wasCurrent) {
        if (next) {
          openVideo(next.videoId, undefined, 'replace');
          focusResult();
        } else {
          navigate('/', 'replace');
          scrollToTop();
        }
      }
      toast('Tab geschlossen', {
        action: {
          label: 'Rückgängig',
          onClick: () => {
            latest.current.openTabs.restore(before);
            if (wasCurrent) navigate(previousPath, 'replace');
          },
        },
      });
    },
    [manager, markUnread, navigate, openVideo, focusResult, toast],
  );

  const closeAllTabs = useCallback(() => {
    const before = latest.current.openTabs.tabs;
    const previousPath = currentPath();
    const wasOnTab = before.some((tab) => isVisible(tab.videoId));
    latest.current.openTabs.closeAll();
    for (const tab of before) manager.remove(tab.videoId);
    sentTabUpdates.current.clear();
    setUnread(new Set());
    if (wasOnTab) navigate('/', 'replace');
    toast(before.length === 1 ? 'Tab geschlossen' : `${before.length} Tabs geschlossen`, {
      action: {
        label: 'Rückgängig',
        onClick: () => {
          latest.current.openTabs.restore(before);
          if (wasOnTab) navigate(previousPath, 'replace');
        },
      },
    });
  }, [manager, navigate, toast]);

  // ------------------------------------------------------------------------
  // Input

  const submitVideo = useCallback(
    (videoId: string, lang?: SummaryLang) => {
      setHint(null);
      setInput('');
      // Opening a video again retries a failed or cancelled summary.
      const existing = manager.get(videoId);
      if (existing && (existing.status === 'error' || existing.status === 'cancelled')) manager.remove(videoId);
      openVideo(videoId, lang);
      focusResult();
    },
    [manager, openVideo, focusResult],
  );

  // A video shared into the already open app (installed PWA): route it here instead of reloading.
  const onLaunchRef = useRef<(url: URL) => void>(() => {});
  onLaunchRef.current = (url: URL) => {
    const target = parseRoute(url.pathname, url.search);
    if (target.kind === 'video') {
      submitVideo(target.videoId, target.lang);
    } else if (target.kind === 'reset-password') {
      navigate(url.pathname + url.search, 'push');
    } else {
      navigate('/', 'push');
      if (target.unrecognized) {
        setInput(target.unrecognized.slice(0, 500));
        setHint({ tone: 'error', text: UNRECOGNIZED_HINT });
      }
    }
  };
  useEffect(() => onLaunch((url) => onLaunchRef.current(url)), []);

  const onInvalid = useCallback((text: string) => {
    setHint(
      text
        ? { tone: 'error', text: INVALID_HINT }
        : { tone: 'info', text: 'Füge zuerst einen YouTube-Link ein.' },
    );
    inputRef.current?.focus();
  }, []);

  const handleText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const id = parseYouTubeVideoId(trimmed);
      if (id) {
        submitVideo(id);
      } else {
        setInput(trimmed);
        onInvalid(trimmed);
      }
    },
    [submitVideo, onInvalid],
  );

  const onPasteButton = useCallback(async () => {
    const text = await readClipboardText();
    if (text == null) {
      setHint({
        tone: 'error',
        text: 'Kein Zugriff auf die Zwischenablage. Halte das Eingabefeld gedrückt und wähle „Einfügen“.',
      });
      inputRef.current?.focus();
      return;
    }
    if (!text.trim()) {
      setHint({ tone: 'info', text: 'Die Zwischenablage ist leer. Kopiere zuerst den Link eines Videos.' });
      return;
    }
    handleText(text);
  }, [handleText]);

  // Ctrl/Cmd+V anywhere on the page (when no field has focus) pastes and starts.
  useEffect(() => {
    let pasteSeen = false;
    const onPaste = (event: ClipboardEvent) => {
      pasteSeen = true;
      if (isEditable(event.target) || isEditable(document.activeElement)) return;
      if (document.querySelector('dialog[open]')) return;
      const text = event.clipboardData?.getData('text/plain') ?? '';
      if (!text.trim()) return;
      event.preventDefault();
      handleText(text);
    };
    // Fallback for browsers that fire no paste event without a focused field.
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'v' || event.altKey || event.shiftKey) return;
      if (isEditable(event.target) || document.querySelector('dialog[open]')) return;
      pasteSeen = false;
      window.setTimeout(async () => {
        if (pasteSeen || !canReadClipboard()) return;
        const text = await readClipboardText();
        if (text) handleText(text);
      }, 80);
    };
    document.addEventListener('paste', onPaste);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [handleText]);

  // ------------------------------------------------------------------------
  // Codex login

  const onCodexConnected = useCallback(
    (result: CodexConnectedResult) => {
      if (latest.current.me?.user) {
        void refreshMe();
      } else if (result.auth) {
        latest.current.localCodex.save(result.auth, result.account);
      }
    },
    [refreshMe],
  );

  const codexResume = useRef(false);
  useEffect(() => {
    codexResume.current = codexDialog.resume;
  }, [codexDialog.resume]);

  const onCodexClose = useCallback(
    (connected: boolean) => {
      const resume = codexResume.current;
      setCodexDialog({ open: false, resume: false });
      if (!connected) return;
      for (const current of Object.values(manager.getSnapshot())) {
        const code = current.status === 'error' ? current.error?.code : undefined;
        // Summaries waiting for a login always resume (also in background tabs; runs with the
        // browser-stored login queue up); after a quota error only the one on screen, when the
        // dialog was opened from that error ("Anderes Konto verbinden").
        const waitingForLogin = code === 'codex_auth_required' || code === 'codex_auth_invalid';
        if (waitingForLogin || (resume && code === 'codex_quota' && isVisible(current.videoId))) {
          manager.retry(current.videoId, { skipCache: true });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manager],
  );

  const disconnectCodex = useCallback(async () => {
    if (codexStatus.location === 'server') {
      try {
        await deleteCodexAuth();
      } catch {
        toast('Die Verbindung konnte nicht getrennt werden. Bitte versuche es erneut.');
        return;
      }
      await refreshMe();
    }
    localCodex.clear();
    toast('ChatGPT-Verbindung getrennt');
  }, [codexStatus.location, localCodex, refreshMe, toast]);

  // Offer once to move a browser-stored login into the account.
  const showMigration =
    !!user && !serverCodex && !!localCodex.login && !migrationHidden && !wasMigrationOffered(user.id);

  const acceptMigration = useCallback(async () => {
    const local = latest.current.localCodex.login;
    if (!user || !local) return;
    markMigrationOffered(user.id);
    setMigrationHidden(true);
    try {
      await saveCodexAuth(local.auth);
      latest.current.localCodex.clear();
      await refreshMe();
      toast('ChatGPT-Verbindung in dein Konto übernommen');
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'codex_auth_invalid') {
        latest.current.localCodex.clear();
        toast('Die gespeicherte Verbindung war abgelaufen. Bitte verbinde ChatGPT neu.');
      } else {
        toast('Übernehmen hat nicht geklappt. Bitte versuche es später erneut.');
      }
    }
  }, [user, refreshMe, toast]);

  // ------------------------------------------------------------------------
  // Account

  const onSignedIn = useCallback(
    async (email: string) => {
      const resume = signInDialog.resume;
      setSignInDialog((state) => ({ ...state, open: false, resume: false, notice: undefined }));
      await refreshMe();
      toast(`Angemeldet als ${email}`);
      if (!resume) return;
      for (const current of Object.values(manager.getSnapshot())) {
        if (current.status === 'error' && current.error?.code === 'login_required') {
          manager.retry(current.videoId, { skipCache: true });
        }
      }
    },
    [refreshMe, signInDialog.resume, manager, toast],
  );

  const onSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      toast(authErrorMessage(error, 'sign-out'));
      return;
    }
    await refreshMe();
    toast('Du bist abgemeldet');
  }, [refreshMe, toast]);

  // ------------------------------------------------------------------------
  // Result actions

  const onErrorAction = useCallback(
    (action: ErrorAction) => {
      if (action === 'connect-codex') setCodexDialog({ open: true, resume: true });
      else if (action === 'sign-in') setSignInDialog({ open: true, view: 'sign-in', resume: true });
      else {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    },
    [],
  );

  const removeFromHistory = useCallback(
    (entry: HistoryEntry) => {
      const before = latest.current.recent.entries;
      recent.remove(entry.videoId, entry.lang);
      toast('Aus dem Verlauf entfernt', { action: { label: 'Rückgängig', onClick: () => recent.restore(before) } });
    },
    [recent, toast],
  );

  const clearHistory = useCallback(() => {
    const before = latest.current.recent.entries;
    recent.restore([]);
    toast('Verlauf gelöscht', { action: { label: 'Rückgängig', onClick: () => recent.restore(before) } });
  }, [recent, toast]);

  const tips = useMemo(() => <Tips />, []);

  return (
    <div className="app">
      <div className="backdrop" aria-hidden="true">
        <span className="backdrop__blob backdrop__blob--1" />
        <span className="backdrop__blob backdrop__blob--2" />
        <span className="backdrop__blob backdrop__blob--3" />
      </div>
      <a href="#main" className="skip-link">
        Zum Inhalt springen
      </a>
      <div className="shell">
        <Header
          user={user}
          loading={me === null}
          codex={codexStatus}
          settings={settings}
          historyCount={recent.entries.length}
          tabs={openTabs.tabs}
          currentVideoId={currentVideoId}
          tabStatus={tabStatus}
          onOpenTab={openTab}
          onCloseTab={(tab) => closeTab(tab.videoId)}
          onCloseAllTabs={closeAllTabs}
          canInstall={installPrompt.canInstall}
          onInstall={() => {
            void installPrompt.install().then((accepted) => {
              if (accepted) toast('App wird installiert');
            });
          }}
          notifications={notificationPermission}
          onEnableNotifications={() => void enableNotifications()}
          onSettingsChange={updateSettings}
          onLoadModels={loadCodexModels}
          onClearHistory={clearHistory}
          onConnectCodex={() => setCodexDialog({ open: true, resume: false })}
          onDisconnectCodex={disconnectCodex}
          onSignIn={() => setSignInDialog({ open: true, view: 'sign-in', resume: false })}
          onSignOut={onSignOut}
          onHome={goHome}
        />

        {showMigration && (
          <div className="banner glass" role="region" aria-label="ChatGPT-Verbindung übernehmen">
            <Sparkles size={20} className="banner__icon" aria-hidden="true" />
            <p className="banner__text">{CODEX_TEXTS.migrateOffer}</p>
            <div className="banner__actions">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  if (user) markMigrationOffered(user.id);
                  setMigrationHidden(true);
                }}
              >
                Nein danke
              </button>
              <button type="button" className="btn btn--primary btn--sm" onClick={acceptMigration}>
                Übernehmen
              </button>
            </div>
          </div>
        )}

        <main id="main" className={`main main--${view}`}>
          {view === 'reset' && route.kind === 'reset-password' ? (
            <ResetPasswordView
              token={route.token}
              linkError={route.error}
              onRequestNewLink={() => {
                navigate('/', 'replace');
                setSignInDialog({ open: true, view: 'forgot', resume: false });
              }}
              onDone={() => {
                navigate('/', 'replace');
                setSignInDialog({
                  open: true,
                  view: 'sign-in',
                  resume: false,
                  notice: 'Dein neues Passwort ist gespeichert. Melde dich jetzt damit an.',
                });
              }}
            />
          ) : view === 'idle' ? (
            <section className="hero" aria-labelledby="hero-title">
              <h1 id="hero-title" className="hero__title">
                YouTube-Videos in Sekunden zusammengefasst
              </h1>
              <p className="hero__lead">Link einfügen – die Zusammenfassung startet sofort.</p>
              <UrlForm
                ref={inputRef}
                value={input}
                onChange={(value) => {
                  setInput(value);
                  if (hint) setHint(null);
                }}
                onVideo={submitVideo}
                onInvalid={onInvalid}
                onPasteButton={onPasteButton}
                hint={hint}
              />
              <AutoFocus target={inputRef} />
              {openTabs.tabs.length > 0 && (
                <section className="open-tabs glass" aria-labelledby="open-tabs-title">
                  <div className="section-head">
                    <h2 id="open-tabs-title" className="section-title">
                      Offene Tabs <span className="section-title__count">{openTabs.tabs.length}</span>
                    </h2>
                    <button type="button" className="btn btn--ghost btn--sm section-head__action" onClick={closeAllTabs}>
                      <X size={16} aria-hidden="true" />
                      Alle schließen
                    </button>
                  </div>
                  <TabList
                    label="Offene Tabs"
                    tabs={openTabs.tabs}
                    status={tabStatus}
                    onOpen={openTab}
                    onClose={(tab) => closeTab(tab.videoId)}
                  />
                </section>
              )}
              {recent.entries.length > 0 ? (
                <HistoryList
                  entries={recent.entries}
                  onOpen={(entry) => {
                    // Explicit language only if the automatic choice would show another one.
                    openVideo(entry.videoId, automaticLang(entry.videoId) === entry.lang ? undefined : entry.lang);
                    window.scrollTo({ top: 0 });
                  }}
                  onRemove={removeFromHistory}
                />
              ) : (
                openTabs.tabs.length === 0 && tips
              )}
            </section>
          ) : (
            job && (
              <div className="workspace">
                <UrlForm
                  ref={inputRef}
                  compact
                  value={input}
                  onChange={(value) => {
                    setInput(value);
                    if (hint) setHint(null);
                  }}
                  onVideo={submitVideo}
                  onInvalid={onInvalid}
                  onPasteButton={onPasteButton}
                  hint={hint}
                />
                <ResultCard
                  ref={resultRef}
                  job={job}
                  signedIn={signedIn}
                  transcriptMode={signedIn ? settings.transcriptMode : 'client'}
                  onCancel={() => manager.cancel(job.videoId)}
                  onRetry={(overrides) => manager.retry(job.videoId, overrides)}
                  onRegenerate={() =>
                    manager.start({
                      videoId: job.videoId,
                      lang: job.summary?.lang ?? job.lang,
                      langPref: settings.langPref,
                      force: true,
                      ...(job.meta ? { meta: job.meta } : {}),
                    })
                  }
                  // Replace: the job (one per video) switches language, so Back could not return to the old one.
                  onChangeLang={(lang) => {
                    if (lang !== job.lang) openVideo(job.videoId, lang, 'replace');
                  }}
                  onAction={onErrorAction}
                  onCopyMarkdown={() => copyText(job.markdown)}
                  // With the language: the recipient sees the same summary, whatever their preference.
                  onCopyLink={() => copyText(absoluteVideoUrl(job.videoId, job.lang))}
                  onNotify={
                    notificationPermission === 'default' || (notificationPermission === 'granted' && !settings.notify)
                      ? () => void enableNotifications()
                      : undefined
                  }
                  closeTab={{
                    onClose: () => closeTab(job.videoId),
                    next: (() => {
                      const next = neighborTab(openTabs.tabs, job.videoId);
                      return next && next.videoId !== job.videoId ? tabTitle(next) : null;
                    })(),
                  }}
                />
              </div>
            )
          )}
        </main>

        <footer className="footer">
          <p>Zusammenfassungen entstehen automatisch und können Fehler enthalten.</p>
        </footer>
      </div>

      <CodexLoginDialog
        open={codexDialog.open}
        signedIn={signedIn}
        resumePending={codexDialog.resume}
        onConnected={onCodexConnected}
        onClose={onCodexClose}
      />
      <SignInDialog
        open={signInDialog.open}
        initialView={signInDialog.view}
        notice={signInDialog.notice}
        onClose={() => setSignInDialog((state) => ({ ...state, open: false, resume: false, notice: undefined }))}
        onSignedIn={onSignedIn}
      />
    </div>
  );
}

/** Focuses the main input on mount (idle view) – not on touch devices, where it would pop up the keyboard. */
function AutoFocus({ target }: { target: RefObject<HTMLInputElement | null> }) {
  useEffect(() => {
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    if (!coarse && !document.querySelector('dialog[open]')) target.current?.focus({ preventScroll: true });
  }, [target]);
  return null;
}

function Tips() {
  const host = window.location.host;
  const touch = window.matchMedia?.('(pointer: coarse)').matches;
  return (
    <ul className="tips" aria-label="So funktioniert es">
      <li className="tip glass">
        <ClipboardPaste size={20} className="tip__icon" aria-hidden="true" />
        <p className="tip__title">Einfach einfügen</p>
        <p className="tip__text">
          {touch
            ? 'Tippe auf „Einfügen“ oder teile ein Video aus der YouTube-App mit dieser App.'
            : 'Strg+V (⌘V) irgendwo auf der Seite genügt – es geht sofort los.'}
        </p>
      </li>
      <li className="tip glass">
        <MonitorSmartphone size={20} className="tip__icon" aria-hidden="true" />
        <p className="tip__title">Direkt aus der Adresszeile</p>
        <p className="tip__text">
          Ersetze in der Adresse eines Videos <span className="nowrap">„youtube.com“</span> durch{' '}
          <span className="nowrap">„{host}“</span>.
        </p>
      </li>
      <li className="tip glass">
        <Sparkles size={20} className="tip__icon" aria-hidden="true" />
        <p className="tip__title">Dein eigenes ChatGPT</p>
        <p className="tip__text">Neue Zusammenfassungen entstehen mit deinem ChatGPT-Abo. Bekannte Videos sind sofort da.</p>
      </li>
    </ul>
  );
}
